import { Box, TextField } from "@mui/material";
import type { ChangeEvent } from "react";
import SsRecoveryCheckSelect from "./SsRecoveryCheckSelect";
import NotDamageSelect from "./NotDamageSelect";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import DatePickerJapanese from "../../../../../../../components/DatePickerJapanese";
import type { DisasterStatusCardProps } from "../../../types";

const DamageStatus = ({
  disasterStatus,
  onDamageStatusChange,
}: Pick<DisasterStatusCardProps, "disasterStatus" | "onDamageStatusChange">) => {
  //被害日時管理
  const damageAt = disasterStatus?.damage_at
    ? new Date(disasterStatus?.damage_at)
    : new Date();
  const handleChangeCalender = (e: Date | null) => {
    if (!onDamageStatusChange) return;
    onDamageStatusChange({
      ...disasterStatus,
      damage_at: e,
    });
  };

  //点検記録簿（SS復旧チェック）実施ステータス管理
  const handleChangeSsRecoveryCheck = (e: number | null) => {
    if (!onDamageStatusChange) return;
    onDamageStatusChange({
      ...(disasterStatus || {}),
      recovery_status: e,
    });
  };

  //被害状況ステータス管理
  const handleChangeNotDamage = (e: boolean | null) => {
    if (!onDamageStatusChange) return;
    onDamageStatusChange({
      ...(disasterStatus || {}),
      not_damage: e,
    });
  };

  //被害状況チェックボックス管理
  const handleChangeCheckbox = (
    e: ChangeEvent<HTMLInputElement>,
    column: string,
  ) => {
    if (!onDamageStatusChange) return;
    const newValue = e.target.checked;
    //親コンポーネントのステート変更
    onDamageStatusChange({
      ...disasterStatus,
      [column]: newValue,
    });
  };

  //被害詳細管理
  const handleChangeTextField = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!onDamageStatusChange) return;
    const newValue = e.target.value;
    //親コンポーネントのステート変更
    onDamageStatusChange({
      ...disasterStatus,
      damage_details: newValue,
    });
  };

  return (
    <Box
      className="child1"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        width: "100%",
        justifyContent: "center",
        borderRadius: 2,
        borderTop: 16,
        borderColor: "primary.main",
        boxShadow: 3,
        color: "outline.dark",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          padding: "16px 40px 32px 40px",
          gap: "16px",
          "@media screen and (max-width:900px)": {
            padding: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 20,
            fontWeight: "bold",
            color: "primary.main",
            whiteSpace: "nowrap",
          }}
        >
          被害状況
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            "@media screen and (max-width:600px)": {
              width: "100%",
              flexDirection: "column",
            },
          }}
        >
          <Box sx={{ width: "248px" }}>被害確認日時</Box>
          <DatePickerJapanese
            type="datetime"
            inputFormat=""
            format="yyyy/MM/dd HH:mm"
            placeholder="****年**月**日"
            value={damageAt}
            onChange={handleChangeCalender}
            column="damage_at"
          />
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            "@media screen and (max-width:900px)": {
              gap: "8px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              "@media screen and (max-width:900px)": {
                gap: "0",
                flexDirection: "column",
                alignItems: "flex-start",
              },
            }}
          >
            <Box
              sx={{
                minWidth: "260px",
                padding: "9px 0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                "@media screen and (max-width:900px)": {
                  padding: "0",
                },
              }}
            >
              点検記録簿（SS復旧チェック）実施
            </Box>
            <Box sx={{ width: "160px" }}>
              <SsRecoveryCheckSelect
                recoveryStatus={disasterStatus?.recovery_status}
                changedValue={(e) => handleChangeSsRecoveryCheck(e)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              "@media screen and (max-width:900px)": {
                gap: "0",
                flexDirection: "column",
                alignItems: "flex-start",
              },
            }}
          >
            <Box
              sx={{
                minWidth: "260px",
                padding: "9px 0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                "@media screen and (max-width:900px)": {
                  padding: "0",
                },
              }}
            >
              被害状況
            </Box>
            <Box sx={{ width: "160px" }}>
              <NotDamageSelect
                notDamage={disasterStatus?.not_damage}
                changedValue={(e) => handleChangeNotDamage(e)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              width: "100%",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  name="personalDamageCheckbox"
                  size="small"
                  checked={disasterStatus?.personal_damage || false}
                  onChange={(e) => handleChangeCheckbox(e, "personal_damage")}
                />
                人的被害あり
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  name="propertyDamageCheckbox"
                  size="small"
                  checked={disasterStatus?.property_damage || false}
                  onChange={(e) => handleChangeCheckbox(e, "property_damage")}
                />
                第三者への物損あり
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  name="facilityDamageCheckbox"
                  size="small"
                  checked={disasterStatus?.facility_damage || false}
                  onChange={(e) => handleChangeCheckbox(e, "facility_damage")}
                />
                SS設備被害あり
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  name="isElectricCheckbox"
                  size="small"
                  checked={disasterStatus?.is_electric == "1" || false}
                  onChange={(e) => handleChangeCheckbox(e, "is_electric")}
                />
                電話・インターネット接続不良有
              </Box>
              {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={disasterStatus?.recovery_status=='1' || false}
                  onChange={(e) => handleChangeCheckbox(e, "recovery_status")}
                />
                点検記録簿（SS復旧チェック）実施で異常無し
              </Box> */}
            </Box>
          </Box>
        </Box>
        {/* <Divider />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              minWidth: "160px",
              padding: "9px 0",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            被害状況（複数選択可）
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              width: "100%",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={disasterStatus?.not_damage || false}
                  onChange={(e) => handleChangeCheckbox(e, "not_damage")}
                />
                被害なし
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={disasterStatus?.personal_damage || false}
                  onChange={(e) => handleChangeCheckbox(e, "personal_damage")}
                />
                人的被害あり
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={disasterStatus?.property_damage || false}
                  onChange={(e) => handleChangeCheckbox(e, "property_damage")}
                />
                第三者への物損あり
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={disasterStatus?.facility_damage || false}
                  onChange={(e) => handleChangeCheckbox(e, "facility_damage")}
                />
                SS設備被害あり
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={disasterStatus?.is_electric == "1" || false}
                  onChange={(e) => handleChangeCheckbox(e, "is_electric")}
                />
                電話・インターネット接続不良有
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={disasterStatus?.recovery_status == "1" || false}
                  onChange={(e) => handleChangeCheckbox(e, "recovery_status")}
                />
                点検記録簿（SS復旧チェック）実施で異常無し
              </Box>
            </Box>
          </Box>
        </Box> */}
        <Divider />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            "@media screen and (max-width:600px)": {
              width: "100%",
              flexDirection: "column",
            },
          }}
        >
          <Box sx={{ width: "248px" }}>被害詳細</Box>
          <TextField
            name="damageDetailsTextField"
            id="outlined-basic"
            variant="outlined"
            multiline
            rows={2}
            value={disasterStatus?.damage_details || ""}
            onChange={(e) => handleChangeTextField(e)}
            sx={{
              height: "100%",
              width: "100%",
              ".MuiInputBase-root": {
                height: "100%",
              },
              "#outlined-basic": {
                boxSizing: "border-box",
                height: "100%",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
export default DamageStatus;
